import { Delete } from '@mui/icons-material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MessageIcon from '@mui/icons-material/Message';
import { Accordion, AccordionDetails, AccordionSummary, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import MuiDialog from '../mui-dialog/MuiDialog';

interface AccordionComponentProps {
	summaryTitle: string;
	summaryTitleFontWeight?: 'normal' | 'bold' | 'bolder' | 'lighter' | number;
	summaryBackgroundColor?: string;
	children: ReactNode;
	hasComment?: boolean;
	showDeleteButton?: boolean;
	deleteButtonTooltip?: string;
	onDelete?: () => void;
	showAddButton?: boolean;
	addButtonTooltip?: string;
	onAdd?: () => void;
	dialogTitle?: string;
	dialogContent?: string;
	forceCollapsed?: boolean;
	onExpandChange?: (isExpanded: boolean) => void;
	// SPEZIALFALL: Button immer anzeigen, aber ggf. Hinweis im Dialog
	forceShowDeleteButton?: boolean;
	deleteDisabledReason?: string;
	deleteDisabledTitle?: string;
}

const MUIAccordionComponent: FC<AccordionComponentProps> = ({
	summaryTitle,
	summaryTitleFontWeight = 'normal',
	summaryBackgroundColor = 'inherit',
	children,
	hasComment = false,
	showDeleteButton = true,
	deleteButtonTooltip,
	onDelete,
	showAddButton = false,
	addButtonTooltip,
	onAdd,
	dialogTitle,
	dialogContent,
	forceCollapsed = false,
	onExpandChange,
	forceShowDeleteButton = false,
	deleteDisabledReason,
	deleteDisabledTitle,
}) => {
	const [openDialog, setOpenDialog] = useState(false);

	const [isExpanded, setIsExpanded] = useState(true); // standardmäßig geöffnet

	// Effekt für das Einklappen aller Accordions
	useEffect(() => {
		if (forceCollapsed) {
			setIsExpanded(false);
		}
	}, [forceCollapsed]);

	const handleOpenDeletedialog = (e: any) => {
		e.stopPropagation();
		setOpenDialog(true);
	};

	const handleDelete = () => {
		setOpenDialog(false);
		// Wenn ein Hinweis gesetzt ist, KEIN Delete ausführen
		if (!deleteDisabledReason && onDelete) {
			onDelete();
		}
	};

	// Diese Funktion wird vom Material-UI Accordion automatisch aufgerufen
	const handleAccordionChange = (_e: React.SyntheticEvent, expanded: boolean) => {
		setIsExpanded(expanded);
		if (onExpandChange) {
			onExpandChange(expanded);
		}
	};

	const handleAdd = (e: any) => {
		e.stopPropagation();
		onAdd && onAdd();
	};

	return (
		<>
			<MuiDialog
				open={openDialog}
				title={
					deleteDisabledReason && deleteDisabledTitle ? deleteDisabledTitle
					: dialogTitle ?
						dialogTitle
					:	'Löschen'
				}
				content={
					deleteDisabledReason ? deleteDisabledReason
					: dialogContent ?
						dialogContent
					:	'Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?'
				}
				onClose={() => setOpenDialog(false)}
				onConfirm={handleDelete}
				confirmButtonDisabled={!!deleteDisabledReason}
			/>
			<Accordion expanded={isExpanded} disableGutters onChange={handleAccordionChange}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: summaryBackgroundColor }}>
					<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
						<Typography sx={{ display: 'flex', alignItems: 'center' }}>
							{<div style={{ fontWeight: summaryTitleFontWeight }}>{summaryTitle}</div>}{' '}
							{hasComment && <MessageIcon fontSize='small' sx={{ marginLeft: '1rem' }} />}
						</Typography>
						{(showDeleteButton || forceShowDeleteButton) && (
							<Tooltip title={deleteButtonTooltip ? deleteButtonTooltip : 'Eintrag löschen'}>
								<IconButton onClick={handleOpenDeletedialog} sx={{ padding: 0, marginRight: '1rem' }}>
									<Delete />
								</IconButton>
							</Tooltip>
						)}
						{showAddButton && (
							<Tooltip title={addButtonTooltip ? addButtonTooltip : 'Neuen Eintrag hinzufügen'}>
								<IconButton onClick={handleAdd} sx={{ padding: 0, marginRight: '1rem' }}>
									<AddCircleOutlineOutlinedIcon />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				</AccordionSummary>
				<AccordionDetails>{children}</AccordionDetails>
			</Accordion>
		</>
	);
};
export default MUIAccordionComponent;
